import { useEffect, useState } from "react";
import {
  Copy, Gift, Lock, AlertTriangle, CheckCircle2, Share2, Link2, QrCode,
  Sparkles, Users, TrendingUp, Megaphone, MessageCircle, Send, Globe,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";

const SHARE_TARGETS = [
  { key: "telegram",  label: "Telegram",  icon: Send,          color: "#229ED9", build: (u, c) => `https://t.me/share/url?url=${encodeURIComponent(u)}&text=${encodeURIComponent(`Join Maxlive88 with my code ${c}`)}` },
  { key: "whatsapp",  label: "WhatsApp",  icon: MessageCircle, color: "#25D366", build: (u, c) => `https://wa.me/?text=${encodeURIComponent(`Join Maxlive88 with my code ${c} — ${u}`)}` },
  { key: "facebook",  label: "Facebook",  icon: Globe,         color: "#1877F2", build: (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}` },
  { key: "twitter",   label: "X / Twitter", icon: Megaphone,   color: "#1DA1F2", build: (u, c) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Join Maxlive88 with my code ${c}`)}&url=${encodeURIComponent(u)}` },
];

export default function PromoCodes() {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: pc } = await api.get("/aff-panel/promo-code");
      setData(pc);
      setEdit(pc.promo_code || "");
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const v = (edit || "").trim().toUpperCase();
    if (!/^[A-Z0-9]{4,12}$/.test(v)) return toast.error("4-12 chars, letters/numbers only");
    setSaving(true);
    try {
      await api.patch("/aff-panel/promo-code", { code: v });
      toast.success("Promo code locked in!");
      setConfirmOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  const copy = (t) => {
    if (!t) return;
    navigator.clipboard.writeText(t).then(() => toast.success("Copied"));
  };

  const share = async (text, url) => {
    if (navigator.share) {
      try { await navigator.share({ title: "Maxlive88", text, url }); return; } catch (_) { /* fall through */ }
    }
    copy(url);
  };

  if (loading || !data) {
    return (
      <div>
        <PageHeader title="Promo Codes" subtitle="Loading…" />
        <div className="p-glass p-skeleton" style={{ height: 280, marginBottom: 16 }} />
        <div className="p-glass p-skeleton" style={{ height: 180 }} />
      </div>
    );
  }

  const locked = data.is_customized;
  const code = data.promo_code || "—";

  return (
    <div>
      <PageHeader
        title="Promo Codes"
        eyebrow="Brand it. Share it. Earn from it."
        subtitle={locked
          ? "Your branded promo code is live and permanently set."
          : "Customize your code once — it becomes permanent after locking."}
      />

      {/* HERO — Promo code showcase */}
      <div
        className="p-glass p-fade-up relative overflow-hidden mb-4"
        style={{
          padding: 0,
          background: locked
            ? "radial-gradient(900px 320px at 0% 0%, rgba(16,185,129,0.12), transparent 65%), radial-gradient(700px 280px at 100% 100%, rgba(34, 192, 115, 0.10), transparent 65%), linear-gradient(180deg, rgba(8,16,12,0.85), rgba(8,8,16,0.95))"
            : "radial-gradient(900px 320px at 0% 0%, rgba(34, 192, 115, 0.16), transparent 65%), linear-gradient(180deg, rgba(28,22,14,0.6), rgba(10,8,16,0.92))",
          border: locked ? "1px solid rgba(16,185,129,0.4)" : "1px solid rgba(34, 192, 115, 0.45)",
        }}
      >
        {/* Decorative grid pattern */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />

        <div className="relative p-5 sm:p-7">
          {/* Status chips */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {locked ? (
              <>
                <span className="p-pill p-pill-emerald" style={{ padding: "5px 12px" }}>
                  <CheckCircle2 size={11} /> Permanent
                </span>
                <span className="p-pill p-pill-emerald" style={{ padding: "5px 12px" }}>
                  <Lock size={10} /> Locked
                </span>
              </>
            ) : (
              <>
                <span className="p-gold-chip">
                  <Gift size={11} /> Auto-generated
                </span>
                <span className="p-pill p-pill-amber" style={{ padding: "5px 12px" }}>
                  <Sparkles size={10} /> Customizable
                </span>
              </>
            )}
          </div>

          {/* The code itself */}
          <div className="text-center sm:text-left mb-5">
            <div className="text-[10px] uppercase tracking-[0.32em] text-white/40 font-bold mb-1.5">Your Code</div>
            <div
              className="font-mono font-black p-gold-text tracking-[0.04em] leading-none break-all"
              style={{
                fontSize: "clamp(36px, 9vw, 64px)",
                textShadow: "0 6px 30px rgba(34, 192, 115, 0.25)",
              }}
              data-testid="promo-code-display"
            >
              {code}
            </div>
            <div className="text-[11.5px] text-white/50 mt-3 max-w-md mx-auto sm:mx-0 leading-snug">
              Share this code with your audience. Every new sign-up using it is{" "}
              <b className="text-emerald-300">permanently credited</b> to your account.
            </div>
          </div>

          {/* Action buttons row */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => copy(code)}
              className="px-4 h-11 rounded-xl text-[12.5px] font-bold inline-flex items-center gap-2 p-btn-ghost"
              data-testid="copy-current-code"
            >
              <Copy size={14} /> Copy Code
            </button>
            <button
              onClick={() => share(`Use my Maxlive88 promo code ${code}`, data.share_link)}
              className="px-4 h-11 rounded-xl text-[12.5px] font-bold inline-flex items-center gap-2 p-btn-gold"
            >
              <Share2 size={14} /> Share
            </button>
            <div className="flex-1" />
          </div>
        </div>
      </div>

      {/* Customize panel */}
      {!locked ? (
        <div
          className="p-glass p-fade-up p-fade-up-1 mb-4"
          style={{
            padding: "22px 24px",
            background: "linear-gradient(180deg, rgba(38,28,8,0.6), rgba(10,8,16,0.9))",
            border: "1px solid rgba(34, 192, 115, 0.5)",
          }}
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{
              background: "linear-gradient(135deg, rgba(34, 192, 115, 0.22), rgba(127, 29, 29, 0.08))",
              border: "1px solid rgba(34, 192, 115, 0.5)",
              color: "#a7f3d0",
            }}>
              <AlertTriangle size={16} />
            </div>
            <div>
              <div className="text-sm font-bold text-emerald-200">One-time customization</div>
              <div className="text-[12.5px] text-white/65 leading-relaxed mt-1">
                You can change your promo code <b className="text-emerald-200">only once</b>. After locking, it becomes permanent.
                If you skip this, the auto-generated code stays active.
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={edit}
              onChange={(e) => setEdit(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              maxLength={12}
              placeholder="YOURBRAND"
              className="flex-1 h-12 px-4 rounded-xl outline-none"
              style={{
                background: "rgba(8,6,14,0.85)",
                border: "1px solid rgba(34, 192, 115, 0.4)",
                color: "#f5f1e6",
                fontFamily: "ui-monospace, Menlo, monospace",
                letterSpacing: "0.18em",
                fontSize: 18,
                fontWeight: 800,
              }}
              data-testid="promo-code-input"
            />
            <button
              onClick={() => {
                const v = (edit || "").trim().toUpperCase();
                if (!/^[A-Z0-9]{4,12}$/.test(v)) { toast.error("4-12 chars, letters/numbers only"); return; }
                if (v === (data.promo_code || "").toUpperCase()) { toast.error("Choose a different code than the current one"); return; }
                setConfirmOpen(true);
              }}
              disabled={saving}
              className="px-6 h-12 rounded-xl inline-flex items-center gap-2 p-btn-gold"
              data-testid="promo-code-save"
            >
              <Lock size={14} /> Lock Code
            </button>
          </div>
          <div className="text-[11px] text-white/45 mt-2">4–12 characters · letters & numbers only · must be globally unique</div>
        </div>
      ) : (
        <div
          className="p-glass p-fade-up p-fade-up-1 mb-4 flex items-center gap-3"
          style={{
            padding: "14px 18px",
            background: "linear-gradient(90deg, rgba(16,185,129,0.10), rgba(16,185,129,0.02))",
            border: "1px solid rgba(16,185,129,0.35)",
          }}
        >
          <Lock size={18} className="text-emerald-300 shrink-0" />
          <div className="text-[12.5px] text-emerald-100/85 leading-snug">
            <b className="text-emerald-200">Your promo code is permanently locked.</b> For changes, contact your account manager.
          </div>
        </div>
      )}

      {/* Share Links — Professional layout */}
      <div className="p-glass p-fade-up p-fade-up-2 mb-4" style={{ padding: "20px 22px" }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Link2 size={14} className="text-emerald-300" />
            <div className="text-sm font-bold text-white">Share Links</div>
          </div>
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-bold">Direct URLs</span>
        </div>
        <div className="space-y-2.5">
          <LinkRow label="Referral Page" url={data.share_link} onCopy={copy} onShare={share} primary />
          <LinkRow label="Short Link" url={data.short_link} onCopy={copy} onShare={share} />
        </div>
      </div>

      {/* Social share quick actions */}
      <div className="p-glass p-fade-up p-fade-up-3 mb-4" style={{ padding: "20px 22px" }}>
        <div className="flex items-center gap-2 mb-1">
          <Megaphone size={14} className="text-emerald-300" />
          <div className="text-sm font-bold text-white">Share on Social</div>
        </div>
        <div className="text-[11px] text-white/50 mb-3">One-tap share to your favorite channels.</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {SHARE_TARGETS.map((t) => {
            const Icon = t.icon;
            return (
              <a
                key={t.key}
                href={t.build(data.share_link, code)}
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center gap-2 px-3 h-11 rounded-xl transition-all hover:scale-[1.02]"
                style={{
                  background: `linear-gradient(135deg, ${t.color}22, ${t.color}08)`,
                  border: `1px solid ${t.color}55`,
                  color: "#f5f1e6",
                  fontWeight: 700,
                  fontSize: 12.5,
                }}
              >
                <Icon size={15} style={{ color: t.color }} />
                <span className="truncate">{t.label}</span>
              </a>
            );
          })}
        </div>
      </div>

      {/* Lock confirmation modal */}
      {confirmOpen && (
        <div className="p-modal-overlay" onClick={() => setConfirmOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="p-modal">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{
                background: "linear-gradient(135deg, rgba(34, 192, 115, 0.22), rgba(127, 29, 29, 0.08))",
                border: "1px solid rgba(34, 192, 115, 0.55)",
                color: "#a7f3d0",
              }}>
                <Lock size={18} />
              </div>
              <div>
                <div className="text-[19px] font-black mb-1 p-platinum-text">Lock this code forever?</div>
                <div className="text-[13px] text-white/65 leading-relaxed">
                  After confirming, your promo code becomes <b className="text-emerald-300">permanent</b> and cannot be changed.
                </div>
              </div>
            </div>
            <div className="rounded-xl p-5 mb-5 text-center" style={{
              background: "radial-gradient(400px 100px at 50% 0%, rgba(34, 192, 115, 0.16), transparent 70%), rgba(34, 192, 115, 0.06)",
              border: "1px solid rgba(34, 192, 115, 0.45)",
            }}>
              <div className="text-[10px] uppercase tracking-[0.28em] text-emerald-200/80 mb-2">New permanent code</div>
              <div className="font-mono text-[36px] font-black p-gold-text tracking-[0.06em]">{edit.toUpperCase()}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setConfirmOpen(false)} disabled={saving} className="flex-1 h-11 rounded-xl p-btn-ghost text-[13px] font-bold">
                Cancel
              </button>
              <button onClick={save} disabled={saving} className="flex-1 h-11 rounded-xl p-btn-gold text-[13px]" data-testid="promo-code-confirm-lock">
                {saving ? "Locking…" : "Confirm & Lock"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LinkRow({ label, url, onCopy, onShare, primary = false }) {
  return (
    <div
      className="flex items-center gap-2 p-3 rounded-xl"
      style={{
        background: primary ? "rgba(34, 192, 115, 0.06)" : "rgba(255,255,255,0.025)",
        border: primary ? "1px solid rgba(34, 192, 115, 0.25)" : "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <div className="flex-1 min-w-0 pr-2">
        <div className="text-[9.5px] uppercase tracking-[0.22em] text-emerald-200/75 font-bold">{label}</div>
        <div
          className="text-[12.5px] text-white truncate font-mono"
          title={url}
          style={{ fontFamily: "ui-monospace, Menlo, monospace" }}
        >
          {url || "—"}
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button onClick={() => onCopy(url)} className="p-2 rounded-lg p-btn-ghost" title="Copy" aria-label="Copy link">
          <Copy size={13} />
        </button>
        <button onClick={() => onShare("Maxlive88 affiliate", url)} className="p-2 rounded-lg p-btn-ghost" title="Share" aria-label="Share link">
          <Share2 size={13} />
        </button>
      </div>
    </div>
  );
}
